/**
 * Colorisation overlay — a colorisation item is scene-shaped (footprint,
 * png_footprint, tif_footprint, skipPreview), so it reuses the exact
 * scene-rendering pipeline (fetchImageFromServer → getCollectionImage →
 * displayFootprintImageNew), which already loads PNG via L.imageOverlay and
 * TIFF via the WMS layer. Only the payload's `action` differs from a normal
 * scene fetch.
 */
export const COLORISATION_LEGEND = [
  { label: 'Man-made and urban areas, bright scattering structures, high texture', color: '#FF0000' },
  { label: 'Vegetation and smoother areas', color: '#00FF00' },
  { label: 'Water, shadows, smooth surfaces, concrete roads, and low-intensity areas', color: '#0000FF' },
  { label: 'Invalid, zero, or non-finite source pixels', color: '#000000' },
];

type ColorisationHost = {
  map: any;
  collection: any;
  imageType: string;
  scenesNew: any[];
  sensorsNew: any[];
  isSensorInList: (sensorId: any) => boolean;
  showColorisationLegend: (show: boolean) => void;
  fetchImageFromServer: (
    collection: any,
    sensorObj: any,
    scene: any,
    isVdapChange?: boolean,
    info?: any
  ) => void;
};

export function isColorisationDelivered(colorisation: any): boolean {
  return String(colorisation?.status ?? '').toLowerCase() === 'delivered';
}

export function isColorisationDisabled(
  host: ColorisationHost,
  sensor: any,
  colorisation: any
): boolean {
  const isSceneChecked = (sensor?.scenes || []).some(
    (scene: any) => scene.id === colorisation?.scene_id && scene.checked === true
  );
  const disabled =
    !isColorisationDelivered(colorisation) ||
    !host.isSensorInList(sensor?.id) ||
    host.sensorsNew.length > 1 ||
    !isSceneChecked;

  // Drop the overlay once its scene/sensor is deselected, like clearVdapImageLayer
  if (disabled && colorisation?.checked) {
    removeColorisationLayer(host, colorisation);
  }
  return disabled;
}

export function toggleColorisationLayer(
  host: ColorisationHost,
  sensor: any,
  colorisation: any,
  checked: boolean
): void {
  // Single selection per sensor, like super resolution
  (sensor?.sarColorisation || []).forEach((item: any) => {
    if (item?.checked) {
      removeColorisationLayer(host, item);
    }
  });

  if (!checked) {
    return;
  }

  colorisation.checked = true;
  colorisation.imageType = host.imageType === 'tif' ? 'tif' : 'png';
  const sensorObj = {
    sensorId: sensor.id,
    sceneId: colorisation.id,
    action: 'colorisation',
    fileType: colorisation.imageType,
  };

  host.fetchImageFromServer(host.collection, sensorObj, colorisation);
  host.showColorisationLegend(true);
}

export function removeColorisationLayer(
  host: ColorisationHost,
  colorisation: any
): void {
  colorisation.checked = false;
  host.showColorisationLegend(false);
  const index = host.scenesNew.findIndex(
    (sceneNew: any) => sceneNew?.id === colorisation?.id
  );
  if (index === -1) {
    return;
  }

  const sceneNew = host.scenesNew[index];
  [
    sceneNew?.leftImageLayer,
    sceneNew?.rightImageLayer,
    sceneNew?.currentWeekImageLayer,
    sceneNew?.footprint,
    sceneNew?.tiffootprint,
  ]
    .filter(Boolean)
    .forEach((layer: any) => host.map.removeLayer(layer));
  host.scenesNew.splice(index, 1);
}
